import { useCallback, useEffect, useState } from 'react';
import type { ConductAppearance } from '@/lib/conduct-presentation';

/** Chave única — Regras, Conduta, Equipe e Fairtrade ficam sempre sincronizadas. */
export const HELP_APPEARANCE_STORAGE_KEY = 'xat-help-appearance';

const LEGACY_KEYS = [
  'xat-regras-appearance',
  'xat-conduta-appearance',
  'xat-equipe-appearance',
] as const;

function readStoredAppearance(): ConductAppearance {
  try {
    const shared = localStorage.getItem(HELP_APPEARANCE_STORAGE_KEY);
    if (shared === 'light' || shared === 'dark') return shared;
    for (const key of LEGACY_KEYS) {
      const legacy = localStorage.getItem(key);
      if (legacy === 'light' || legacy === 'dark') return legacy;
    }
  } catch {
    /* ignore */
  }
  return 'dark';
}

function writeStoredAppearance(next: ConductAppearance) {
  try {
    localStorage.setItem(HELP_APPEARANCE_STORAGE_KEY, next);
    for (const key of LEGACY_KEYS) {
      localStorage.setItem(key, next);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Aparência clara/escura partilhada entre /regras, /conduta, /equipe e /fairtrade.
 * Sincroniza via `storage` entre abas e migra chaves antigas.
 */
export function useHelpAppearance() {
  const [appearance, setAppearance] = useState<ConductAppearance>('dark');
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setAppearance(readStoredAppearance());
    setHydrated(true);

    const onStorage = (e: StorageEvent) => {
      if (
        e.key !== HELP_APPEARANCE_STORAGE_KEY &&
        !LEGACY_KEYS.includes(e.key as (typeof LEGACY_KEYS)[number])
      ) {
        return;
      }
      if (e.newValue === 'light' || e.newValue === 'dark') {
        setAppearance(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setMode = useCallback((next: ConductAppearance) => {
    setAppearance(next);
    writeStoredAppearance(next);
  }, []);

  const isLight = hydrated && appearance === 'light';

  return { appearance, setMode, hydrated, isLight };
}
